import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AuthenticationResult } from '@azure/msal-browser';

import { msalInstance } from './auth/msalConfig';
import { queryClient } from './lib/queryClient';
import { router } from './router';
import { LightboxProvider } from './features/attachments/Lightbox';
import './index.css';

// Wire MSAL: ensure handleRedirectPromise has resolved before render so any
// in-flight sign-in (loginRedirect callback) is consumed before the router
// decides where to send the user.
//
// Crucially this is wrapped so a redirect-handling failure (stale/aborted
// interaction state, misconfigured authority, etc.) NEVER prevents the app
// from rendering — otherwise an MSAL throw here aborts module evaluation and
// the user gets a blank page instead of the login screen.
try {
  await msalInstance.initialize();
  const result = await msalInstance.handleRedirectPromise();
  if (result?.account) {
    msalInstance.setActiveAccount(result.account);
  } else if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts()[0]) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }
} catch (err) {
  // Render anyway; the user lands on /login and can retry sign-in.
  console.error('[msal] redirect handling failed; rendering app regardless:', err);
}
msalInstance.addEventCallback((evt) => {
  if (evt.eventType === EventType.LOGIN_SUCCESS && evt.payload) {
    const payload = evt.payload as AuthenticationResult;
    msalInstance.setActiveAccount(payload.account);
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <LightboxProvider>
          <RouterProvider router={router} />
        </LightboxProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
      </QueryClientProvider>
    </MsalProvider>
  </StrictMode>
);
